// Locale state — the single owner of "which language is the UI in".
//
// Resolution order (design spec §9): the /de|/fr|/it path prefix → ?lang= (embeds
// pin their language) → English if the URL is a /d/ dataset page → the stored
// preference (ds-lang) → en. The browser language is NOT consulted; see
// resolveLocale for why.
// label() in lib/meta.ts consults getLocale() on every call, so switching
// locale + re-rendering is enough; no data refetch (the client always fetches
// FULL label objects — that is also what lets search match across languages).

export const LOCALES = ["en", "de", "fr", "it"] as const;
export type Locale = (typeof LOCALES)[number];

let current: Locale = "en";
const KEY = "ds-lang";

const asLocale = (v: unknown): Locale | null =>
  typeof v === "string" && (LOCALES as readonly string[]).includes(v) ? (v as Locale) : null;

/** Resolve the boot locale. Pure; pass window pieces in for testability.
 *
 * THE URL DECIDES, then the visitor's own choice:
 *   /de/d/<id> path  ->  ?lang=  ->  stored preference  ->  en
 *
 * The browser's Accept-Language is deliberately NOT consulted. Google's
 * hreflang cluster already serves a German searcher the /de/ URL, so the search
 * path is covered without guessing; and a URL that renders differently per
 * visitor is unshareable. What remains is explicit: the address you opened, or
 * the language you picked. `stored` is the SELECTOR's memory, not a guess —
 * when it applies, sharePath() upgrades the URL to match, so the two never
 * disagree for long.
 *
 * INSIDE AN EMBED the stored preference is skipped too, mirroring the theme
 * rule in web/index.html: an iframe runs on our origin and so can read the
 * visitor's localStorage, but their choice on dataseries.org must not follow
 * the chart onto someone else's site. An embed takes ?lang= from the embedder,
 * else English. */
export function resolveLocale(
  search: string,
  hash: string,
  stored: string | null,
  pathname = "",
): Locale {
  // /de/d/<id> (a dataset page) and /de/ (the homepage) both state their language.
  const p = /^\/(de|fr|it)(?:\/|$)/.exec(pathname || "");
  const q = search + hash;
  const m = /[?&]lang=([a-z]{2})/.exec(q);
  const embed = /[?&]embed=/.test(q);
  // English lives at the bare path for all three shapes: /d/<id>, "/" and
  // /datasets/maps are the English pages, canonical and tagged hreflang="en".
  // None may yield to a stored preference, or a shared English link opens in
  // German for anyone who once picked German.
  const urlStatesLanguage =
    /^\/(?:(?:de|fr|it)\/)?d\//.test(pathname || "") || pathname === "/" ||
    /^\/datasets\/maps\/?$/.test(pathname || "");
  return (
    asLocale(p && p[1]) ??
    asLocale(m && m[1]) ??
    (urlStatesLanguage ? "en" : null) ??
    (embed ? null : asLocale(stored)) ??
    "en"
  );
}

export function initLocale(): Locale {
  let stored: string | null = null;
  try { stored = localStorage.getItem(KEY); } catch { /* embeds may sandbox storage */ }
  current = resolveLocale(location.search, location.hash, stored, location.pathname);
  document.documentElement.lang = current;
  return current;
}

export const getLocale = (): Locale => current;

/** Set + persist the locale. The caller re-renders. */
export function setLocale(l: Locale): void {
  current = l;
  try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  document.documentElement.lang = l;
}

// ---- UI chrome strings -------------------------------------------------------
// Keyed by the English string (msgid = en): a missing entry falls through to the
// key itself, so extraction is incremental and a new string can ship en-first.
// Data-derived strings (titles, labels, units) do NOT go here — they come from
// the data and resolve through label().
const DE: Record<string, string> = {
  "All {n} datasets, by topic and provider": "Alle {n} Datensätze, nach Thema und Quelle",
  "Switzerland's economic\ndata, in one place.": "Wirtschaftsdaten der\nSchweiz, an einem Ort.",
  "Try": "Beispiele",
  "Datasets": "Datensätze",
  "About": "Über uns",
  "Libraries": "Bibliotheken",
  "Language": "Sprache",
  "Appearance": "Darstellung",
  "Developed by": "Entwickelt von",
  "Data updated": "Daten aktualisiert",
  "{n} datasets": "{n} Datensätze",
  "Last successful scrape:": "Letzter erfolgreicher Abruf:",
  "Open Swiss economic data, in one place. Sourced from the national statistical providers.": "Offene Schweizer Wirtschaftsdaten, an einem Ort. Direkt von den nationalen Statistikstellen.",
  "Level": "Niveau",
  "Index": "Index",
  "YoY %": "Vorjahr %",
  "QoQ %": "Vorquartal %",
  "MoM %": "Vormonat %",
  "% change": "% Veränderung",
  "1Y": "1J",
  "5Y": "5J",
  "10Y": "10J",
  "level": "Niveau",
  "year-on-year change, %": "Veränderung zum Vorjahr, %",
  "quarter-on-quarter change, %": "Veränderung zum Vorquartal, %",
  "month-on-month change, %": "Veränderung zum Vormonat, %",
  "change vs previous period, %": "Veränderung zur Vorperiode, %",
  "YoY pp": "Vorjahr Pp.",
  "QoQ pp": "Vorquartal Pp.",
  "MoM pp": "Vormonat Pp.",
  "change, pp": "Veränderung, Pp.",
  "year-on-year change, percentage points": "Veränderung zum Vorjahr, Prozentpunkte",
  "quarter-on-quarter change, percentage points": "Veränderung zum Vorquartal, Prozentpunkte",
  "month-on-month change, percentage points": "Veränderung zum Vormonat, Prozentpunkte",
  "change vs previous period, percentage points": "Veränderung zur Vorperiode, Prozentpunkte",
  "index, viewport start = 100": "Index, Fensterbeginn = 100",
  "share of GDP, %": "Anteil am BIP, %",
  "Last updated": "Zuletzt aktualisiert",
  "Checked": "Geprüft",
  "Series": "Serien",
  "{n} available": "{n} verfügbar",
  "monthly": "monatlich",
  "quarterly": "quartalsweise",
  "annual": "jährlich",
  "daily": "täglich",
  "weekly": "wöchentlich",
  "semi-annual": "halbjährlich",
  "Search…": "Suchen…",
  "Search series…": "Serien suchen…",
  "Search": "Suchen",
  "Clear": "Löschen",
  "Results": "Ergebnisse",
  "Popular series": "Beliebte Serien",
  "All datasets": "Alle Datensätze",
  "Main datasets": "Hauptdatensätze",
  "Maps": "Karten",
  "Map": "Karte",
  "Show on map": "Auf der Karte zeigen",
  "{n} datasets and {m} maps, by topic and provider.": "{n} Datensätze und {m} Karten, nach Thema und Quelle.",
  "Datasets and maps": "Datensätze und Karten",
  "{n} map": "{n} Karte",
  "{n} maps": "{n} Karten",
  "Also as a chart:": "Auch als Diagramm:",
  "Population map": "Bevölkerungskarte",
  "Loading…": "Wird geladen…",
  "no data for this combination": "keine Daten für diese Kombination",
  "No data with the current type/adjustment": "Keine Daten mit der aktuellen Auswahl",
  "No data for": "Keine Daten für",
  "Not defined for values of zero or below. Use the Level view.": "Für Werte von null oder darunter nicht definiert. Die Ansicht «Niveau» zeigt die Werte.",
  "Up to {n} comparison series": "Höchstens {n} Vergleichsreihen",
  "{n} comparison series on the chart": "{n} Vergleichsreihen im Diagramm",
  "and {n} more": "und {n} weitere",
  "Click to remove from the chart": "Klicken, um aus dem Diagramm zu entfernen",
  "Add to the chart": "Zum Diagramm hinzufügen",
  "Reset to defaults": "Auf Standard zurücksetzen",
  "Use this series": "Diese Serie verwenden",
  "Download": "Herunterladen",
  "More": "Mehr",
  "Copy": "Kopieren",
  "Copied": "Kopiert",
  "Copy a link to this chart": "Link zu diesem Diagramm kopieren",
  "Click to rename this chart": "Klicken, um das Diagramm umzubenennen",
  "Details": "Details",
  "Single series": "Einzelne Serie",
  "Breakdown": "Gliederung",
  "Embed": "Einbetten",
  "Topic": "Thema",
  "Source": "Quelle",
  "Frequency": "Frequenz",
  "Coverage": "Abdeckung",
  "Auto": "Auto",
  "Light": "Hell",
  "Dark": "Dunkel",
  "part of …": "Teil von …",
  "click to open": "Klick zum Öffnen",
  "Max": "Max",
  "just now": "gerade eben",
  "{n} minute ago": "vor {n} Minute",
  "{n} hour ago": "vor {n} Stunde",
  "{n} day ago": "vor {n} Tag",
  "{n} minutes ago": "vor {n} Minuten",
  "{n} hours ago": "vor {n} Stunden",
  "{n} days ago": "vor {n} Tagen",
};
const FR: Record<string, string> = {
  "All {n} datasets, by topic and provider": "Les {n} jeux de données, par thème et par source",
  "Switzerland's economic\ndata, in one place.": "Donn\u00e9es \u00e9conomiques\nsuisses, r\u00e9unies.",
  "Try": "Essayez",
  "Datasets": "Jeux de données",
  "About": "À propos",
  "Libraries": "Bibliothèques",
  "Language": "Langue",
  "Appearance": "Apparence",
  "Developed by": "Développé par",
  "Data updated": "Données mises à jour",
  "{n} datasets": "{n} jeux de données",
  "Last successful scrape:": "Dernière collecte réussie :",
  "Open Swiss economic data, in one place. Sourced from the national statistical providers.": "Les données économiques suisses ouvertes, réunies. Issues des offices statistiques nationaux.",
  "Level": "Niveau",
  "Index": "Indice",
  "YoY %": "sur 1 an %",
  "QoQ %": "sur 1 trim. %",
  "MoM %": "sur 1 mois %",
  "% change": "% variation",
  "1Y": "1A",
  "5Y": "5A",
  "10Y": "10A",
  "level": "niveau",
  "year-on-year change, %": "variation sur un an, %",
  "quarter-on-quarter change, %": "variation trimestrielle, %",
  "month-on-month change, %": "variation mensuelle, %",
  "change vs previous period, %": "variation par rapport à la période précédente, %",
  "YoY pp": "sur 1 an pp",
  "QoQ pp": "sur 1 trim. pp",
  "MoM pp": "sur 1 mois pp",
  "change, pp": "variation, pp",
  "year-on-year change, percentage points": "variation sur un an, points de pourcentage",
  "quarter-on-quarter change, percentage points": "variation trimestrielle, points de pourcentage",
  "month-on-month change, percentage points": "variation mensuelle, points de pourcentage",
  "change vs previous period, percentage points": "variation par rapport à la période précédente, points de pourcentage",
  "index, viewport start = 100": "indice, début de la fenêtre = 100",
  "share of GDP, %": "part du PIB, %",
  "Last updated": "Dernière mise à jour",
  "Checked": "Vérifié",
  "Series": "Séries",
  "{n} available": "{n} disponibles",
  "monthly": "mensuelle",
  "quarterly": "trimestrielle",
  "annual": "annuelle",
  "daily": "quotidienne",
  "weekly": "hebdomadaire",
  "semi-annual": "semestrielle",
  "Search…": "Rechercher…",
  "Search series…": "Rechercher des séries…",
  "Search": "Rechercher",
  "Clear": "Effacer",
  "Results": "Résultats",
  "Popular series": "Séries populaires",
  "All datasets": "Tous les jeux de données",
  "Main datasets": "Jeux de données principaux",
  "Maps": "Cartes",
  "Map": "Carte",
  "Show on map": "Voir sur la carte",
  "{n} datasets and {m} maps, by topic and provider.": "{n} jeux de données et {m} cartes, par thème et par source.",
  "Datasets and maps": "Jeux de données et cartes",
  "{n} map": "{n} carte",
  "{n} maps": "{n} cartes",
  "Also as a chart:": "Aussi en graphique :",
  "Population map": "Carte de la population",
  "Loading…": "Chargement…",
  "no data for this combination": "pas de données pour cette combinaison",
  "No data with the current type/adjustment": "Pas de données avec la sélection actuelle",
  "No data for": "Pas de données pour",
  "Not defined for values of zero or below. Use the Level view.": "Non défini pour les valeurs nulles ou négatives. La vue « Niveau » affiche les valeurs.",
  "Up to {n} comparison series": "{n} séries de comparaison au plus",
  "{n} comparison series on the chart": "{n} séries de comparaison dans le graphique",
  "and {n} more": "et {n} autres",
  "Click to remove from the chart": "Cliquer pour retirer du graphique",
  "Add to the chart": "Ajouter au graphique",
  "Reset to defaults": "Rétablir les valeurs par défaut",
  "Use this series": "Utiliser cette série",
  "Download": "Télécharger",
  "More": "Plus",
  "Copy": "Copier",
  "Copied": "Copié",
  "Copy a link to this chart": "Copier un lien vers ce graphique",
  "Click to rename this chart": "Cliquer pour renommer le graphique",
  "Details": "Détails",
  "Single series": "Série unique",
  "Breakdown": "Ventilation",
  "Embed": "Intégrer",
  "Topic": "Thème",
  "Source": "Source",
  "Frequency": "Fréquence",
  "Coverage": "Couverture",
  "Auto": "Auto",
  "Light": "Clair",
  "Dark": "Sombre",
  "part of …": "partie de …",
  "click to open": "cliquer pour ouvrir",
  "Max": "Max",
  "just now": "à l'instant",
  "{n} minute ago": "il y a {n} minute",
  "{n} hour ago": "il y a {n} heure",
  "{n} day ago": "il y a {n} jour",
  "{n} minutes ago": "il y a {n} minutes",
  "{n} hours ago": "il y a {n} heures",
  "{n} days ago": "il y a {n} jours",
};
const IT: Record<string, string> = {
  "All {n} datasets, by topic and provider": "Tutti i {n} set di dati, per tema e fonte",
  "Switzerland's economic\ndata, in one place.": "Dati economici svizzeri,\nin un solo posto.",
  "Try": "Prova",
  "Datasets": "Set di dati",
  "About": "Informazioni",
  "Libraries": "Librerie",
  "Language": "Lingua",
  "Appearance": "Aspetto",
  "Developed by": "Sviluppato da",
  "Data updated": "Dati aggiornati",
  "{n} datasets": "{n} set di dati",
  "Last successful scrape:": "Ultima raccolta riuscita:",
  "Open Swiss economic data, in one place. Sourced from the national statistical providers.": "I dati economici svizzeri aperti, in un unico posto. Dagli uffici statistici nazionali.",
  "Level": "Livello",
  "Index": "Indice",
  "YoY %": "su 1 anno %",
  "QoQ %": "su 1 trim. %",
  "MoM %": "su 1 mese %",
  "% change": "% variazione",
  "1Y": "1A",
  "5Y": "5A",
  "10Y": "10A",
  "level": "livello",
  "year-on-year change, %": "variazione su base annua, %",
  "quarter-on-quarter change, %": "variazione trimestrale, %",
  "month-on-month change, %": "variazione mensile, %",
  "change vs previous period, %": "variazione rispetto al periodo precedente, %",
  "YoY pp": "su 1 anno pp",
  "QoQ pp": "su 1 trim. pp",
  "MoM pp": "su 1 mese pp",
  "change, pp": "variazione, pp",
  "year-on-year change, percentage points": "variazione su base annua, punti percentuali",
  "quarter-on-quarter change, percentage points": "variazione trimestrale, punti percentuali",
  "month-on-month change, percentage points": "variazione mensile, punti percentuali",
  "change vs previous period, percentage points": "variazione rispetto al periodo precedente, punti percentuali",
  "index, viewport start = 100": "indice, inizio finestra = 100",
  "share of GDP, %": "quota del PIL, %",
  "Last updated": "Ultimo aggiornamento",
  "Checked": "Verificato",
  "Series": "Serie",
  "{n} available": "{n} disponibili",
  "monthly": "mensile",
  "quarterly": "trimestrale",
  "annual": "annuale",
  "daily": "giornaliera",
  "weekly": "settimanale",
  "semi-annual": "semestrale",
  "Search…": "Cerca…",
  "Search series…": "Cerca serie…",
  "Search": "Cerca",
  "Clear": "Cancella",
  "Results": "Risultati",
  "Popular series": "Serie popolari",
  "All datasets": "Tutti i set di dati",
  "Main datasets": "Set di dati principali",
  "Maps": "Mappe",
  "Map": "Mappa",
  "Show on map": "Mostra sulla mappa",
  "{n} datasets and {m} maps, by topic and provider.": "{n} set di dati e {m} mappe, per tema e fonte.",
  "Datasets and maps": "Set di dati e mappe",
  "{n} map": "{n} mappa",
  "{n} maps": "{n} mappe",
  "Also as a chart:": "Anche come grafico:",
  "Population map": "Mappa della popolazione",
  "Loading…": "Caricamento…",
  "no data for this combination": "nessun dato per questa combinazione",
  "No data with the current type/adjustment": "Nessun dato con la selezione attuale",
  "No data for": "Nessun dato per",
  "Not defined for values of zero or below. Use the Level view.": "Non definito per valori nulli o negativi. La vista «Livello» mostra i valori.",
  "Up to {n} comparison series": "Al massimo {n} serie di confronto",
  "{n} comparison series on the chart": "{n} serie di confronto nel grafico",
  "and {n} more": "e altri {n}",
  "Click to remove from the chart": "Clicca per rimuovere dal grafico",
  "Add to the chart": "Aggiungi al grafico",
  "Reset to defaults": "Ripristina i valori predefiniti",
  "Use this series": "Usa questa serie",
  "Download": "Scarica",
  "More": "Altro",
  "Copy": "Copia",
  "Copied": "Copiato",
  "Copy a link to this chart": "Copia un link a questo grafico",
  "Click to rename this chart": "Clicca per rinominare il grafico",
  "Details": "Dettagli",
  "Single series": "Serie singola",
  "Breakdown": "Suddivisione",
  "Embed": "Incorpora",
  "Topic": "Tema",
  "Source": "Fonte",
  "Frequency": "Frequenza",
  "Coverage": "Copertura",
  "Auto": "Auto",
  "Light": "Chiaro",
  "Dark": "Scuro",
  "part of …": "parte di …",
  "click to open": "clicca per aprire",
  "Max": "Max",
  "just now": "proprio ora",
  "{n} minute ago": "{n} minuto fa",
  "{n} hour ago": "{n} ora fa",
  "{n} day ago": "{n} giorno fa",
  "{n} minutes ago": "{n} minuti fa",
  "{n} hours ago": "{n} ore fa",
  "{n} days ago": "{n} giorni fa",
};

// Exported so chrome-coverage.test.ts can check key PRESENCE. Comparing t()'s
// output against the key cannot tell "translated to the same word" (de "Index",
// fr "Source") from "not translated at all".
export const CATALOGS: Partial<Record<Locale, Record<string, string>>> = { de: DE, fr: FR, it: IT };

/** Translate a UI chrome string; en (or an untranslated key) passes through. */
export function t(key: string): string {
  const c = CATALOGS[current];
  return (c && c[key]) || key;
}

/** t() with a {n} placeholder. */
export const tn = (key: string, n: number): string => t(key).replace("{n}", String(n));

/** Month abbreviations for chart axes/tooltips, per locale. */
const MONTHS: Record<Locale, string[]> = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  de: ["Jan", "Feb", "März", "Apr", "Mai", "Juni", "Juli", "Aug", "Sep", "Okt", "Nov", "Dez"],
  fr: ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"],
  it: ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"],
};
export const monthsShort = (): string[] => MONTHS[current];

/** BCP-47 tag for number/date formatting (Swiss conventions). */
export const numberLocale = (): string =>
  ({ en: "en-GB", de: "de-CH", fr: "fr-CH", it: "it-CH" })[current];
